import {
	AuthenticationResult,
	PublicClientApplication,
} from '@azure/msal-browser';

import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { msalConfig } from 'config/msalConfig';
import packageJson from '../../package.json';
import { cleanupStorage, getLanguageStorage } from './localStorage';

let reduxStore: ToolkitStore;
const tokenInstance = new PublicClientApplication(msalConfig);

export const injectReduxStore = (_store: ToolkitStore) => {
	reduxStore = _store;
};

const authFetch = axios.create({
	baseURL: process.env.REACT_APP_API_URL,
});

axiosRetry(authFetch, {
	retries: 4,
	retryDelay: retryCount => retryCount * 500,
	retryCondition(error) {
		//FIXME: to be used once BE will send us more specific error code (501, 502, etc)
		switch (error?.response?.status) {
			case 500:
				return true;
			default:
				return false;
		}
	},
});

export const acquireAccessToken = async () => {
	const activeAccount = tokenInstance.getActiveAccount();
	const accounts = tokenInstance.getAllAccounts();

	const account = activeAccount || accounts[0];
	if (!account) {
		throw new Error('User not logged in');
	}

	const request = {
		scopes: [],
		account: account,
	};

	await tokenInstance.initialize();
	let authResult: AuthenticationResult;
	try {
		authResult = await tokenInstance.acquireTokenSilent(request);
	} catch (err: any) {
		if (
			['interaction_required', 'login_required', 'consent_required'].includes(
				err?.errorCode,
			)
		) {
			cleanupStorage();
			tokenInstance.acquireTokenRedirect(request);
		}
		throw err;
	}

	const token = `Bearer ${authResult.idToken}`;

	authFetch.defaults.headers.common['Authorization'] = token;

	return token;
};

authFetch.interceptors.request.use(
	async config => {
		const { version } = packageJson;
		const typeRegex = /test|qa|preview/;
		const envTypeIdx = version.search(typeRegex);

		if (!config.headers!.Authorization || (config as any).retryAndRefreshToken) {
			config.headers!.Authorization = await acquireAccessToken();
		}

		// If token is expired first call throw 401 and second one loose content-type 'multipart/form-data'
		// this condition manages this case
		// SORRY the mess :'(
		if (config.data instanceof FormData) {
			config.headers!['Content-Type'] =
				config.headers!['Content-Type'] || 'multipart/form-data';
		} else {
			config.headers!['Content-Type'] =
				config.headers!['Content-Type'] || 'application/json';
		}

		config.headers!['Mercurio-Client-Type'] =
			config.headers!['Mercurio-Client-Type'] || 'provider-portal';

		config.headers!['Accept-Language'] = getLanguageStorage();

		if (envTypeIdx > -1) {
			const envType = version.slice(envTypeIdx, version.length);
			config.headers!['Mercurio-Client-Env'] = envType;
		}

		if (reduxStore) {
			const { user } = reduxStore.getState();
			const { profile } = user;

			if (profile) {
				config.headers!['providerId'] =
					config.headers!['providerId'] || profile?.providerId;
				config.headers!['networkId'] =
					config.headers!['networkId'] || profile?.networkId;
			}
		}
		return config;
	},
	error => {
		console.error('error', error);
		return Promise.reject(error);
	},
);

authFetch.interceptors.response.use(
	response => response,
	async error => {
		switch (error.status) {
			case 401:
				const retryAndRefreshToken = error.config?.retryAndRefreshToken;

				if (!retryAndRefreshToken) {
					return authFetch.request({
						...error.config,
						retryAndRefreshToken: true,
					});
				}
				break;
		}

		return Promise.reject(error);
	},
);

export const checkForUnauthorizedResponse = (
	error: { response: string },
	thunkAPI: any,
) => {
	console.error('error', error);
	return thunkAPI.rejectWithValue(error.response);
};

export default authFetch;
